import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CheckCircle2, Mail } from "lucide-react";
import { toast } from "sonner";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      setSent(true);
    } catch {
      toast.error("Submission failed. Try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-40 border-b border-border bg-card/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="font-display font-black text-xl">Rule<span className="text-primary">AI</span><span className="text-muted-foreground">.systems</span></Link>
          <Link to="/"><Button variant="ghost" size="sm" className="rounded-sm">← Home</Button></Link>
        </div>
      </header>
      <div className="max-w-2xl mx-auto px-6 py-20">
        {sent ? (
          <div className="border border-emerald-200 bg-emerald-50 dark:bg-emerald-950/20 p-10 text-center rounded-sm" data-testid="contact-sent">
            <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto" />
            <h1 className="font-display text-3xl font-bold mt-4">Thanks, we&rsquo;ll be in touch.</h1>
            <p className="text-sm text-muted-foreground mt-3">
              We reply to every message within one business day.
            </p>
            <Link to="/"><Button className="mt-8 rounded-sm bg-primary hover:bg-primary/90">Back to home</Button></Link>
          </div>
        ) : (
          <>
            <div className="overline text-primary">/ CONTACT</div>
            <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight mt-2">Talk to us.</h1>
            <p className="text-muted-foreground mt-4">
              Questions, demos, partnerships, or support — we read every message.
            </p>
            <form onSubmit={submit} className="mt-10 space-y-4" data-testid="contact-form">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Name</Label>
                  <Input required data-testid="contact-name" className="rounded-sm mt-1.5" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input required type="email" data-testid="contact-email" className="rounded-sm mt-1.5" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} />
                </div>
              </div>
              <div>
                <Label>Company (optional)</Label>
                <Input data-testid="contact-company" className="rounded-sm mt-1.5" value={form.company} onChange={(e) => setForm({...form, company: e.target.value})} />
              </div>
              <div>
                <Label>Message</Label>
                <Textarea required rows={6} data-testid="contact-message" className="rounded-sm mt-1.5" value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} />
              </div>
              <Button type="submit" disabled={busy} data-testid="contact-submit" className="rounded-sm bg-primary hover:bg-primary/90 h-11 px-6">
                <Mail className="w-4 h-4 mr-2" /> {busy ? "Sending…" : "Send message"}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
